import { useUserDetails } from "@repo/api";
import { Skeleton, Text, type TextProps } from "@repo/skapa";
import {
  getAbbreviationIndex,
  getFullName,
  getInitials,
  maskName,
} from "@repo/utils";
import { useEffect, useState } from "react";

import { useSettings } from "../settings/SettingsProvider";

interface Props {
  email?: string;
  emails?: string[];
  textStyle?: TextProps["textStyle"];
  tagName?: TextProps["tagName"];
  color?: string;
  bold?: boolean;
  fragment?: boolean;
  defaultValue?: string;
}

export const useCoWorkerName = (
  email: string | undefined,
  defaultValue?: string,
  emails?: string[]
) => {
  const { general, feature_flags, customerMode, isLoadingSettings } =
    useSettings();
  const { data, isLoading: isLoadingUserDetails } = useUserDetails(email);

  const [name, setName] = useState(defaultValue ?? undefined);
  const [initials, setInitials] = useState<string | undefined>(undefined);

  const abbreviationIndex = getAbbreviationIndex(email, emails);

  // keep loading until name has been decided
  const hasName = Boolean(name);
  const isLoading =
    isLoadingUserDetails || isLoadingSettings || (!hasName && email);

  useEffect(() => {
    if (data && (data.givenName || data.surname)) {
      const displayName = getFullName(
        data.givenName,
        data.surname,
        general?.localization?.reverse_names,
        customerMode,
        abbreviationIndex,
        feature_flags?.abbreviate_coworker_name
      );

      setName(displayName);
      setInitials(
        getInitials(
          data.givenName,
          data.surname,
          general?.localization?.reverse_names
        )
      );
    } else if (email) {
      // If no names returned, decide the stable fallback once and stop loading

      setName(customerMode ? maskName(email) : email);
    } else {
      // eslint-disable-next-line no-console
      console.error("No email found", {
        email,
        data,
        name,
      });
      setName(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, feature_flags, general, abbreviationIndex, customerMode, email]);

  if (!email) {
    return {
      name: undefined,
      initials: undefined,
      abbreviationIndex,
      isLoading: false,
    };
  }

  return {
    name,
    initials,
    abbreviationIndex,
    isLoading,
  };
};

// This component displays a coworker's name based on their email.
// It supports abbreviating or masking the email based on feature flags.

export const CoWorkerName = ({
  email,
  emails,
  textStyle,
  tagName,
  color,
  bold,
  fragment,
  defaultValue,
}: Props) => {
  const { name, isLoading } = useCoWorkerName(email, defaultValue, emails);

  if (fragment) {
    // Avoid flicker for fragment
    if (isLoading) {
      return <Skeleton width="100%" />;
    }
    return <>{name}</>;
  }

  if (isLoading) {
    return <Skeleton width="100%" />;
  }

  return (
    <Text
      textStyle={textStyle}
      tagName={tagName}
      color={color}
      noWrap
      ellipsis
      bold={bold}
    >
      {name}
    </Text>
  );
};
